// Catalog.ID Client — Credential Display Component
// ============================================================================
// Displays labeled credential values with copy, print, and download actions.
// Used for supersecret export during registration and in key management.

import { createElement, createCopyButton } from '@catalog-shell';

export interface CredentialField {
    label: string;
    value: string;
    monospace?: boolean;  // default: true
}

export interface CredentialDisplayConfig {
    fields: CredentialField[];
    /** If set, download/print uses these fields instead of `fields` (for including extra data like keys). */
    downloadFields?: CredentialField[];
    showPrint?: boolean;
    showDownload?: boolean;
    downloadFilename?: string;
    /**
     * Optional footer appended to the downloaded/printed text. Defaults to a
     * supersecret-oriented warning about identity being irrecoverable. Pass a
     * custom string for credentials that can be rotated (e.g., API keys).
     */
    downloadFooter?: string;
}

export function createCredentialDisplay(config: CredentialDisplayConfig): HTMLElement {
    const container = createElement('div', { className: 'cid-credential-display' });

    for (const field of config.fields) {
        const row = createElement('div', { className: 'cid-credential-row' });

        const label = createElement('div', {
            className: 'cid-credential-label',
            textContent: field.label,
        });
        row.appendChild(label);

        const valueRow = createElement('div', { className: 'cid-credential-value-row' });

        const mono = field.monospace !== false;
        const value = createElement('div', {
            className: `cid-credential-value${mono ? ' cid-credential-value--mono' : ''}`,
            textContent: field.value,
        });
        valueRow.appendChild(value);

        const copyBtn = createCopyButton({ getText: () => field.value });
        valueRow.appendChild(copyBtn);

        row.appendChild(valueRow);
        container.appendChild(row);
    }

    // Action buttons
    const actions = createElement('div', { className: 'cid-credential-actions' });

    const DEFAULT_FOOTER = 'Keep this file in a secure location. If you lose your credentials and keys and have no device with imported keys, your identity is irrecoverable.';
    const footer = config.downloadFooter ?? DEFAULT_FOOTER;

    if (config.showPrint !== false) {
        const printBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cid-credential-action',
            textContent: 'Print',
        });
        printBtn.addEventListener('click', () => printCredentials(config.downloadFields ?? config.fields, footer));
        actions.appendChild(printBtn);
    }

    if (config.showDownload !== false) {
        const dlBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cid-credential-action',
            textContent: 'Download',
        });
        dlBtn.addEventListener('click', () => downloadCredentials(config.downloadFields ?? config.fields, config.downloadFilename, footer));
        actions.appendChild(dlBtn);
    }

    container.appendChild(actions);
    return container;
}

function printCredentials(fields: CredentialField[], footer: string): void {
    const html = fields.map(f =>
        f.label
            ? `<div style="margin-bottom: 16px;">
                <div style="font-weight: bold; margin-bottom: 4px;">${escapeHtml(f.label)}</div>
                <div style="font-family: monospace; font-size: ${f.value.length > 100 ? '9' : '16'}px; padding: 8px; background: #f5f5f5; border: 1px solid #ddd; word-break: break-all; white-space: pre-wrap;">${escapeHtml(f.value)}</div>
            </div>`
            : '<hr style="border: none; border-top: 1px solid #ddd; margin: 16px 0;">'
    ).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    printWindow.document.write(`
        <html><head><title>Catalog.ID Credentials</title></head>
        <body style="font-family: sans-serif; padding: 40px; max-width: 500px;">
            <h2 style="color: #333;">Catalog.ID Credentials</h2>
            ${html}
            <p style="color: #999; font-size: 12px; margin-top: 32px;">
                ${escapeHtml(footer)}
            </p>
        </body></html>
    `);
    printWindow.document.close();
    printWindow.print();
}

function downloadCredentials(fields: CredentialField[], filename: string | undefined, footer: string): void {
    const text = fields.map(f => f.label ? `${f.label}: ${f.value}` : '').join('\n') +
        `\n\n${footer}\n`;

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? 'catalog-id-credentials.txt';
    a.click();
    URL.revokeObjectURL(url);
}

function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
